import {FC, useState, useMemo} from 'react'
import {Helmet} from 'react-helmet-async'
import {Link} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {KTIcon, toAbsoluteUrl} from '../../../_metronic/helpers'

const FAQ_KEYS = [
  {q: 'FAQ.Q1', a: 'FAQ.A1'},
  {q: 'FAQ.Q2', a: 'FAQ.A2'},
  {q: 'FAQ.Q3', a: 'FAQ.A3'},
  {q: 'FAQ.Q4', a: 'FAQ.A4'},
  {q: 'FAQ.Q5', a: 'FAQ.A5'},
  {q: 'FAQ.Q6', a: 'FAQ.A6'},
  {q: 'FAQ.Q7', a: 'FAQ.A7'},
  {q: 'FAQ.Q8', a: 'FAQ.A8'},
  {q: 'FAQ.Q9', a: 'FAQ.A9'},
  {q: 'FAQ.Q10', a: 'FAQ.A10'},
]

const FAQPage: FC = () => {
  const intl = useIntl()
  const [open, setOpen] = useState<number | undefined>(0)
  const faqData = useMemo(
    () => FAQ_KEYS.map(({q, a}) => ({q: intl.formatMessage({id: q}), a: intl.formatMessage({id: a})})),
    [intl]
  )
  return (
    <>
      <Helmet>
        <title>{intl.formatMessage({id: 'FAQ.META.TITLE'})}</title>
        <meta name='description' content={intl.formatMessage({id: 'FAQ.META.DESC'})} />
        <meta property='og:title' content={intl.formatMessage({id: 'FAQ.META.TITLE'})} />
        <meta property='og:description' content={intl.formatMessage({id: 'FAQ.META.DESC'})} />
      </Helmet>

      {/* Hero Section */}
      <section className='hero-section contact-hero mt-2'>
        <div className='hero-content'>
          <div className='mb-4'>
            <span className='badge px-4 py-2 rounded-pill'>
              <KTIcon iconName='question' className='fs-2 text-warning ms-2' />
              {intl.formatMessage({id: 'FAQ.HERO.BADGE'})}
            </span>
          </div>
          <h1 className='hero-title'>
            {intl.formatMessage({id: 'FAQ.HERO.TITLE'})}
            <br />
            {intl.formatMessage({id: 'FAQ.HERO.SUBTITLE'})}
          </h1>
          <p className='hero-desc'>
            {intl.formatMessage({id: 'FAQ.HERO.DESC'})}
          </p>
        </div>
        <div className='hero-image'>
          <img
            src={toAbsoluteUrl('/media/illustrations/energy/Img_FAQs_L.webp')}
            alt={intl.formatMessage({id: 'FAQ.HERO.BADGE'})}
            style={{maxWidth: 570}}
          />
        </div>
        <button
          className='hero-scroll-btn'
          onClick={() => {
            const nextSection = document.getElementById('first-content-section')
            if (nextSection) nextSection.scrollIntoView({behavior: 'smooth'})
          }}
          aria-label={intl.formatMessage({id: 'COMMON.SCROLL_DOWN'})}
        >
          <svg
            className='hero-scroll-svg'
            width='120'
            height='64'
            viewBox='0 0 120 64'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M88.0935 28C88.0935 12.536 75.3811 0 59.6996 0C44.0181 0 31.3057 12.536 31.3057 28C31.3057 43.464 44.0181 56 59.6996 56C75.3811 56 88.0935 43.464 88.0935 28Z'
              fill='#F1F1F3'
            ></path>
            <path
              d='M39.5969 8.86421C32.3121 15.6129 24.4015 27.5594 4.6961 27.8366C-15.0093 28.1137 33.673 39.7129 33.673 39.7129C33.673 39.7129 46.8818 2.11406 39.5969 8.86421Z'
              fill='#F1F1F3'
            ></path>
            <path
              d='M80.4024 8.86412C86.625 16.1764 95.5975 27.5595 115.304 27.8366C135.01 28.1138 86.3273 39.7129 86.3273 39.7129C86.3273 39.7129 74.1798 1.55253 80.4024 8.86412Z'
              fill='#F1F1F3'
            ></path>
            <path
              d='M54.6667 22.3334L60 27.6667L65.3334 22.3334'
              stroke='#19191A'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            ></path>
          </svg>
        </button>
      </section>

      {/* FAQ Content */}
      <section id='first-content-section' className='py-20'>
        <div className='container'>
          <div className='row justify-content-center'>
            <div className='col-lg-10 col-xl-8'>
              <div className='faq-accordion'>
                {faqData.map((item, idx) => (
                  <div
                    key={idx}
                    className={`card shadow-sm mb-4 ${open === idx ? 'border-primary' : ''}`}
                  >
                    <div
                      className='card-header p-3 d-flex align-items-center justify-content-between'
                      style={{cursor: 'pointer'}}
                      onClick={() => setOpen(open === idx ? undefined : idx)}
                    >
                      <span className='fw-semibold fs-5'>{item.q}</span>
                      <span className='fs-3 text-primary'>{open === idx ? '-' : '+'}</span>
                    </div>
                    {open === idx && (
                      <div className='card-body pt-2 pb-4 fs-6 text-gray-700'>{item.a}</div>
                    )}
                  </div>
                ))}
              </div>
              <div className='text-center mt-8'>
                <Link to='/contact' className='btn btn-primary px-8 py-3 fw-semibold'>
                  {intl.formatMessage({id: 'FAQ.CTA'})}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
export {FAQPage}
